import { Component, inject, OnInit, signal } from '@angular/core';
import { ServicePortalApi } from '../../../api/api';
import { CustomerAccount } from '../../../api/types';
import { Router, RouterLink } from '@angular/router';
import {
  SynBreadcrumbComponent,
  SynBreadcrumbItemComponent, SynButtonComponent, SynIconComponent,
  SynInputComponent, SynOptionComponent, SynSelectComponent,
} from '@synergy-design-system/angular';
import { ColumnDescription, TableComponent } from '../../components/table';
import { Location } from '@angular/common';
import { getUserData } from '../../../api/auth';
import { FormsModule } from '@angular/forms';
import { errorMessage } from '../../../errors';

function getValue(str: string): string | undefined {
  if (str.length === 0) return undefined;
  return str;
}

@Component({
  selector: 'app-export-customer-accounts',
  template: `
    <syn-breadcrumb>
      <syn-breadcrumb-item routerLink="" i18n="@@home">Home</syn-breadcrumb-item>
      <syn-breadcrumb-item routerLink="/customers" i18n="@@customer-accounts">Customer Accounts</syn-breadcrumb-item>
      <syn-breadcrumb-item i18n="@@export">Export</syn-breadcrumb-item>
    </syn-breadcrumb>

    <h1 i18n="@@export-customer-accounts">Export Customer Accounts</h1>

    <form class="container" (submit)="onSearch()">
      <div class="flex-col gap-1 margin-bottom-1">
        <syn-input
          [(value)]="query"
          label="Text Search" i18n-label="@@text-search"
        />
          <syn-select
            [(value)]="ssuCode"
            label="SSU Code" i18n-label="@@ssu-code"
            [clearable]="false" [disabled]="managedSsuOptions().length  === 1"
          >
            @for (ssu of managedSsuOptions(); track ssu) {
              <syn-option [value]="ssu">{{ ssu }}</syn-option>
            }
          </syn-select>

        <syn-input
          [(value)]="customerNumber"
          label="Customer Number" i18n-label="@@customer-number"
        />
        <syn-select
          [(value)]="enabled"
          label="Enabled" i18n-label="@@enabled"
          [clearable]="true"
        >
          <syn-option value="enabled" i18n="@@enabled">Enabled</syn-option>
          <syn-option value="disabled" i18n="@@disabled">Disabled</syn-option>
        </syn-select>

        <div class="flex-row gap-1">
          <syn-button type="submit" variant="filled" class="flex-grow-1">
            <syn-icon name="search_fill" slot="prefix" />
            <span i18n="@@search">Search</span>
          </syn-button>
          <syn-button (click)="onSave()" variant="outline"
          >
            <syn-icon name="save_fill" slot="prefix" />
            <span i18n="@@download-as-csv">Download as CSV</span>
          </syn-button>
        </div>
      </div>
    </form>

    <app-table
      [columns]="columns"
      [values]="accounts()"
      [pageSize]="20"
      [clickable]="false"
      [search]="false"
      [loading]="loading()"
      [(searchTextField)]="query"
    />
  `,
  styles: [
    `
      .container {
        max-width: 640px;
      }
    `,
  ],
  imports: [
    RouterLink,
    SynBreadcrumbComponent,
    SynBreadcrumbItemComponent,
    TableComponent,
    SynInputComponent,
    SynSelectComponent,
    SynOptionComponent,
    SynButtonComponent,
    SynIconComponent,
    FormsModule,
  ],
})
export class ExportCustomerAccountsComponent implements OnInit {
  api = inject(ServicePortalApi);
  router = inject(Router);
  location = inject(Location);

  accounts = signal<CustomerAccount[]>([]);
  loading = signal(false);
  isAdmin = signal(false);

  query = signal('');
  ssuCode = signal('');
  customerNumber = signal('');
  enabled = signal('');
  managedSsuOptions = signal<string[]>([]);

  columns: ColumnDescription<CustomerAccount>[] = [
    {
      label: $localize`:@@username:Username`,
      getField: (account) => account.username,
      style: 'width: 15%',
    },
    {
      label: $localize`:@@email:Email`,
      getField: (account) => account.email ?? '',
      style: 'width: 15%',
    },
    {
      label: $localize`:@@first-name:First Name`,
      getField: (account) => account.firstName,
      style: 'width: 10%',
    },
    {
      label: $localize`:@@last-name:Last Name`,
      getField: (account) => account.lastName,
      style: 'width: 10%',
    },
    {
      label: $localize`:@@customer-name:Customer Name`,
      getField: (account) => account.customerName ?? '',
      style: 'width: 15%',
    },
    {
      label: $localize`:@@customer-number:Customer Number`,
      getField: (account) => account.customerNumber ?? '',
      style: 'width: 10%',
    },
    {
      label: $localize`:@@last-login:Last Login`,
      getField: (account) => account.lastLogin ?? '',
      style: 'width: 15%',
    },
    {
      label: $localize`:@@enabled:Enabled`,
      getField: (account) => account.enabled ? '✔' : '✖',
      style: 'width: 5%',
    },
    {
      label: $localize`:@@email-verified:Email Verified`,
      getField: (account) => account.emailVerified ? '✔' : '✖',
      style: 'width: 5%',
    },
  ];

  ngOnInit(): void {
    const user = getUserData();
    const ssuCode = user.ssuCode ?? '';
    this.ssuCode.set(ssuCode);
    this.isAdmin.set(user.roles.includes('sickid_usermanager_admin'));
    if (this.isAdmin()) {
      this.api.getSsus(({ ssus }) => {
        this.managedSsuOptions.set(ssus.toSorted());
      });
    } else if (user.managedSsu && user.managedSsu.trim().length > 0) {
      const options = user.managedSsu
        .split(',')
        .map((s) => s.trim())
        .filter((s) => s.length > 0);
      this.managedSsuOptions.set(options);
    } else {
      this.managedSsuOptions.set([this.ssuCode()]);
    }
  }

  onSearch() {
    const query = this.query();
    const ssuCode = getValue(this.ssuCode());
    const customerNumber = getValue(this.customerNumber());
    let enabled = undefined;
    if (this.enabled().length > 0) {
      enabled = this.enabled() === 'enabled';
    }


    this.loading.set(true);
    this.api.exportCustomers({
      query,
      ssuCode,
      customerNumber,
      enabled,
    }, (result) => {
      this.accounts.set(result.accounts);
      this.loading.set(false);
    }, (error) => {
      console.log(error);
      errorMessage.set($localize`:@@error-exporting-customers:Exporting customers took too long. Try to narrow down your search criteria or try again later.`);
      this.loading.set(false);
    });
  }

  onSave() {
    const sep = ',';
    const titles = ['Username', 'Email', 'First Name', 'Last Name',
      'Customer Name', 'Customer Number', 'Last Login', 'Enabled', 'Email Verified'];
    const csv =
      titles.join(sep) + '\n' +
      this.accounts().map((account) => {
        return [account.username, account.email ?? '',
          account.firstName, account.lastName,
          account.customerName ?? '', account.customerNumber ?? '',
          account.lastLogin ?? '', account.enabled ? 'true' : 'false',
          account.emailVerified ? 'true' : 'false']
          .map((value) => '"' + value.replaceAll('"', '\\"') + '"')
          .join(sep);
      }).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });

    const elem = window.document.createElement('a');
    elem.href = window.URL.createObjectURL(blob);
    elem.download = 'customers-export.csv';
    document.body.appendChild(elem);
    elem.click();
    document.body.removeChild(elem);
  }
}
